import { useQuery } from '@tanstack/react-query';

import type { DashboardDateRange } from '@/components/dashboard/model';
import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { dashboardRangeKey } from '../../dashboard/hooks';
import {
  supersedableDashboardQueryFn,
  useAbortSupersededDashboardQueries,
} from '../../dashboard/supersededDashboardRequests';
import { createPublisherDashboardClient } from './api';

export const publisherDashboardQueryKey = ['publisher', 'dashboard'] as const;
export const publisherDetailsDashboardQueryKey = ['publisher', 'details-dashboard'] as const;

export function usePublisherDashboardQueries(publisherId: string, range: DashboardDateRange) {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const client = createPublisherDashboardClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session?.accessToken ?? '',
  });
  const enabled = Boolean(session?.accessToken && publisherId);
  const rangeKey = dashboardRangeKey(range);
  const queryKey = [...publisherDashboardQueryKey, publisherId, 'report', rangeKey];
  const reportQuery = {
    queryKey,
    // Reusable across StrictMode remounts; aborted only when the date range moves on.
    queryFn: supersedableDashboardQueryFn(queryKey, (signal) =>
      client.report(publisherId, range, signal),
    ),
    enabled,
  };
  const overview = useQuery({ ...reportQuery, select: (data) => data.overview });
  const inventory = useQuery({ ...reportQuery, select: (data) => data.inventory });
  useAbortSupersededDashboardQueries([queryKey]);
  return { overview, inventory };
}

export function usePublisherDetailsDashboardQueries(
  publisherId: string,
  range: DashboardDateRange,
) {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const client = createPublisherDashboardClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session?.accessToken ?? '',
  });
  const enabled = Boolean(session?.accessToken && publisherId);
  const rangeKey = dashboardRangeKey(range);
  const overviewKey = [...publisherDetailsDashboardQueryKey, publisherId, 'overview', rangeKey];
  const demandSourcesKey = [
    ...publisherDetailsDashboardQueryKey,
    publisherId,
    'demand-sources',
    rangeKey,
  ];
  const overview = useQuery({
    queryKey: overviewKey,
    queryFn: supersedableDashboardQueryFn(overviewKey, (signal) =>
      client.detailsOverview(publisherId, range, signal),
    ),
    enabled,
  });
  const demandSources = useQuery({
    queryKey: demandSourcesKey,
    queryFn: supersedableDashboardQueryFn(demandSourcesKey, (signal) =>
      client.detailsDemandSources(publisherId, range, signal),
    ),
    enabled,
  });
  useAbortSupersededDashboardQueries([overviewKey, demandSourcesKey]);
  return { overview, demandSources };
}
